import ExcelJS from 'exceljs'
import iconv from 'iconv-lite'

import { RoomEntity, RoomSelectRegistrationEntity, SelectMasterEntity } from 'src/types'

type CreateCsvDataInput = {
  projectName: string
  selectMasters: SelectMasterEntity[]
  rooms: RoomEntity[]
  allRoomSelectRegistrations: RoomSelectRegistrationEntity[]
}

export const useNtSystemCsvDownload = () => {
  const createCellData = (
    room: RoomEntity,
    selectMaster: SelectMasterEntity,
    roomSelectRegistrations: RoomSelectRegistrationEntity[]
  ) => {
    if (room.publishApplicationRequestStatus !== 'approved') {
      return ''
    }
    const targetRoomSelectRegistration = roomSelectRegistrations.find(r => r.id === room.id)
    const targetSelectRegistration = targetRoomSelectRegistration?.selects.find(
      s => s.selectMasterId === selectMaster.id
    )

    const option = targetSelectRegistration?.registeredSelectOption

    // 有償がある場合はその旨を記載
    let name = option?.name ?? ''
    if (option?.isPaid) {
      name = `${name}（有償）`
    }

    // 子回答がある場合もその旨を記載
    if (option?.child) {
      name = `${name}/${option.child.name}`
      if (option.child.isPaid) {
        name = `${name}（有償）`
      }
    }

    return name
  }

  const createCsvData = async (input: CreateCsvDataInput) => {
    const { projectName, selectMasters, rooms, allRoomSelectRegistrations } = input

    const workbook = new ExcelJS.Workbook()
    workbook.addWorksheet('sheet1')
    const worksheet = workbook.getWorksheet('sheet1')

    if (!worksheet) {
      return
    }

    // 間取りセレクトのある/なしによってセルの表示を切り替える必要がある
    const planSelectSelectMasters = selectMasters.filter(s => s.isPlanSelect)
    const nonPlanSelectSelectMasters = selectMasters.filter(s => !s.isPlanSelect)
    const hasPlanSelect = planSelectSelectMasters.length > 0

    // 間取りセレクトのある/なしによって列数が変わる
    const selectMasterColCount = hasPlanSelect
      ? // 間取りセレクトがONの設問は1列にまとめて表示する
        1 + nonPlanSelectSelectMasters.length
      : selectMasters.length
    worksheet.addRow([
      projectName,
      '',
      ...Array.from({ length: selectMasterColCount }).map(() => ''),
    ])

    // 間取りセレクトがONの設問は、3列目に間取りセレクトとしてまとめて表示する必要がある
    const headCellTitles = hasPlanSelect
      ? ['部屋番号', '部屋タイプ', '間取りセレクト', ...nonPlanSelectSelectMasters.map(s => s.name)]
      : ['部屋番号', '部屋タイプ', ...selectMasters.map(s => s.name)]
    worksheet.addRow(headCellTitles)

    for (const room of rooms) {
      const row = [room.name, room.roomType?.name ?? '']

      // 間取りセレクトがONの設問は、1つにまとめて表示する
      if (hasPlanSelect) {
        const planSelectCell = planSelectSelectMasters
          .map(selectMaster => createCellData(room, selectMaster, allRoomSelectRegistrations))
          // 空文字（選択していない設問）は除外
          .filter(s => s !== '')
          .join('・')
        row.push(planSelectCell)
      }

      // 間取りセレクトがOFFの設問は、1つ1つ表示する
      const nonPlanSelectCells = nonPlanSelectSelectMasters.map(selectMaster =>
        createCellData(room, selectMaster, allRoomSelectRegistrations)
      )
      row.push(...nonPlanSelectCells)

      worksheet.addRow(row)
    }

    // CR+LF 改行コードを指定して CSV ファイルを生成する
    const uint8Array = await workbook.csv.writeBuffer({
      formatterOptions: { rowDelimiter: '\r\n' },
    })
    const shiftJisBuffer = iconv.encode(uint8Array.toString(), 'MS932')
    return new Blob([new Uint8Array(shiftJisBuffer)], { type: 'application/octet-binary' })
  }

  return {
    createCsvData,
  }
}
